import { getFragmentData } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { Wizard } from '../../components/Wizard';
import type { ServiceUsagesTotalCardCmp_TotalCalculation_Fragment } from '../../components/ServiceUsages/TotalCard';

import { FormVariant } from '../enums/variant.enum';

import { ResolvableMissionTypeFormTab } from './enums/tab.enum';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { ResolvableMissionTypeForm_ResolvableMissionType_Fragment } from './fragments/resolvable-mission-type.fragment';
import { useResolvableMissionTypeSubmit } from './hooks/useSubmit.hook';
import { useResolvableMissionTypeSteps } from './hooks/useSteps.hook';
import type { ResolvableMissionTypeMutationResult } from './hooks/useSubmit.hook';

export { ResolvableMissionTypeFormTab };

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly formVariant: FormVariant;
  readonly initialValues?: FragmentType<
    typeof ResolvableMissionTypeForm_ResolvableMissionType_Fragment
  >;
  readonly resolvableMissionTypeId?: Id;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: ResolvableMissionTypeMutationResult) => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly stepIdsSubset?: readonly ResolvableMissionTypeFormTab[];
  readonly title?: string;
  readonly totalCalculation?: FragmentType<
    typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
  > | null;
}

export const ResolvableMissionTypeForm = ({
  askMemoBeforeSubmit = true,
  formVariant,
  initialValues: ppInitialValues,
  resolvableMissionTypeId,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  setMustBlockAction,
  showCancelButton = true,
  stepIdsSubset,
  title,
  totalCalculation,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || resolvableMissionTypeId),
    'You must provide a resolvableMissionTypeId when formVariant is Modification',
  );

  const pInitialValues = getFragmentData(
    ResolvableMissionTypeForm_ResolvableMissionType_Fragment,
    ppInitialValues,
  );

  // initial values
  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const { handleSubmit } = useResolvableMissionTypeSubmit({
    formVariant,
    handleSuccess,
    resolvableMissionTypeId,
  });

  const { steps, wizardChildren } = useResolvableMissionTypeSteps({
    formVariant,
    initialValues,
    stepIdsSubset,
    totalCalculation,
  });

  return (
    <Wizard
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      formTitle={title}
      formVariant={formVariant}
      initialValues={initialValues}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      setMustBlockAction={setMustBlockAction}
      showCancelButton={showCancelButton}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
